import { isSamePasswordMessage } from '@/lib/inviteAccountBranch';

export type InviteAcceptScope = 'staff' | 'portal';

type MappedInviteCode = 'not-found' | 'unauthorized' | 'invalid-input' | 'conflict';

function isMappedInviteCode(code: string): code is MappedInviteCode {
  return (
    code === 'not-found' ||
    code === 'unauthorized' ||
    code === 'invalid-input' ||
    code === 'conflict'
  );
}

/**
 * Map accept-invitation / accept-patient-portal error codes to i18n keys.
 * Never returns a server or GoTrue message.
 */
export function inviteAcceptErrorI18nKey(code: string, scope: InviteAcceptScope): string {
  const prefix = scope === 'staff' ? 'auth.accept_invitation' : 'portal.accept';
  if (!isMappedInviteCode(code)) return 'auth.errors.unknown';
  switch (code) {
    case 'not-found':
      return `${prefix}.invalid_or_expired`;
    case 'unauthorized':
      return `${prefix}.existing_user_password`;
    case 'invalid-input':
      return `${prefix}.bad_input`;
    case 'conflict':
      return `${prefix}.please_retry`;
    default: {
      const _exhaustive: never = code;
      return _exhaustive;
    }
  }
}

function errorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : '';
  }
  return '';
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : '';
}

/**
 * Map supabase.auth.updateUser password failures to i18n keys.
 * Classifies internally; never surfaces the raw English string.
 */
export function resetPasswordErrorI18nKey(err: unknown): string {
  const code = errorCode(err);
  const message = errorMessage(err);
  if (code === 'same_password' || isSamePasswordMessage(message)) {
    return 'auth.reset_password.same_password';
  }
  if (
    code === 'session_not_found' ||
    message.toLowerCase().includes('session missing') ||
    message.toLowerCase().includes('auth session')
  ) {
    return 'auth.reset_password.link_expired_body';
  }
  if (message.toLowerCase().includes('at least')) {
    return 'auth.reset_password.min_length';
  }
  return 'auth.reset_password.save_failed';
}

export function recoveryRedirectLocation(
  pathname: string,
  search: string,
  hash: string,
): { pathname: string; search: string; hash: string } | null {
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const isRecovery =
    query.get('type') === 'recovery' ||
    hashParams.get('type') === 'recovery' ||
    query.has('token_hash');
  if (!isRecovery) return null;
  return {
    pathname: pathname.startsWith('/portal') ? '/portal/reset-password' : '/reset-password',
    search,
    hash,
  };
}
